// src/pages/builder/DynamicTreasureMapPage.js
//
// Route: /dynamic-treasure-map/:state/:grade/:subject/:view/:targetSlug
// (a topic's 🎮 Game button lands here). Shows the walking treasure map
// (components/treasure/TreasureTrail.js) for this class:
//   * topics in the layout's order, minus any the teacher hid (live — the
//     map changes the moment the teacher saves Educator Controls)
//   * a chest opens and stays open once that topic's game is finished
//     (live from userData)
//   * free accounts: topics past the free allowance show a lock
//   * "Play" opens the game with ?from=map, so its Return comes back here
//   * a student's spot on the map is saved on their row of the class list
//     (educatorRosters/{code}/students/{uid}.mapSpots), so…
//   * TEACHER VIEW: every student's avatar stands at their spot, each chest
//     shows how many students opened it, and a chest's card lists who did
//     (live — a chest opens on the teacher's map the moment a kid finishes)

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { orderTopics } from "../../utils/standardOrder";
import { useNavigate, useParams } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { getPortalLayout, makeLayoutId } from "../../services/layoutService";
import { lookupEducatorByCode } from "../../utils/educatorCode";
import useAccessLevel from "../../hooks/useAccessLevel";
import AdminBackButton from "../../components/AdminBackButton";
import CornerCoins from "../../components/coins/CornerCoins";
import TreasureTrail from "../../components/treasure/TreasureTrail";
import FunPopup from "../../components/common/FunPopup";
import GiveCoinsPopup from "../../components/coins/GiveCoinsPopup";
import { GoalEditor, KidCard } from "../../components/treasure/MapTeacherPopups";
import { subjectLabel } from "../../data/subjects";
import "../../styles/admin-back-button.css";
import "./layoutBuilder.css";

const DynamicTreasureMapPage = () => {
  const { state, grade, subject, view, targetSlug } = useParams();
  const navigate = useNavigate();
  const { contentLimit, contentLimitForCourse, isAdmin } = useAccessLevel();

  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hiddenMap, setHiddenMap] = useState(null); // teacher's topicVisibility
  const [progress, setProgress] = useState({});
  const [uid, setUid] = useState("");
  const [pop, setPop] = useState(null);
  const [myCode, setMyCode] = useState(""); // student: their teacher's code; teacher: their own
  const [roster, setRoster] = useState([]); // teacher view: the class list rows
  const [kidProgress, setKidProgress] = useState({}); // teacher view: uid → { slug: true }
  const [goal, setGoal] = useState(null); // the class goal for this subject (live)
  const [goalOpen, setGoalOpen] = useState(false);
  const [kidOpen, setKidOpen] = useState(""); // teacher: whose card is open
  const [giveTo, setGiveTo] = useState(null); // teacher: Give coins popup for [uid]

  const layoutId = useMemo(() => makeLayoutId({ state, grade, subject, view }), [state, grade, subject, view]);
  const subjectKey = `${state}_${grade}_${subject}`;
  const portalPath = `/portal/${state}/${grade}/${subject}/${view}`;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getPortalLayout(layoutId)
      .then((d) => alive && setLayout(d))
      .catch((e) => console.error("Error loading treasure map layout:", e))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [layoutId]);

  // who's here; their progress (live) and — for a student — their teacher's
  // hide/show choices (live)
  useEffect(() => {
    let stops = [];
    const stopAuth = onAuthStateChanged(getAuth(), async (user) => {
      stops.forEach((f) => f());
      stops = [];
      if (!user) return;
      setUid(user.uid);
      stops.push(
        onSnapshot(
          doc(db, "userData", user.uid),
          (snap) => {
            const all = (snap.exists() && snap.data()) || {};
            const mine = all[subjectKey] || {};
            const map = {};
            Object.entries(mine).forEach(([slug, e]) => {
              if (!e || typeof e !== "object") return;
              const g = e.game;
              map[slug] = { game: typeof g === "boolean" ? g : Boolean(g?.completed) };
            });
            setProgress(map);
          },
          () => {}
        )
      );
      if (!layout) return;
      try {
        let ownerUid = view === "educator" ? user.uid : null;
        const me = await getDoc(doc(db, "users", user.uid));
        const code = me.exists() ? String(me.data().educatorCode || "") : "";
        setMyCode(code);
        if (view === "student") {
          const ed = code ? await lookupEducatorByCode(code) : null;
          ownerUid = ed?.uid || null;
        }
        if (!ownerUid) return;
        const id = `${ownerUid}_${layout.state}_${layout.grade}_${layout.subject}`;
        stops.push(
          onSnapshot(
            doc(db, "educatorPortalControls", id),
            (snap) => setHiddenMap((snap.exists() && snap.data().topicVisibility) || null),
            () => {}
          )
        );
      } catch (e) {
        console.warn("[map] couldn't read the teacher's controls:", e?.message || e);
      }
    });
    return () => {
      stopAuth();
      stops.forEach((f) => f());
    };
  }, [layout, view, subjectKey]);

  // TEACHER: the class list (live) …
  useEffect(() => {
    if (view !== "educator" || !myCode) return undefined;
    return onSnapshot(
      collection(db, "educatorRosters", myCode, "students"),
      (snap) =>
        setRoster(
          snap.docs
            .map((d) => ({ uid: d.id, ...d.data() }))
            .filter((r) => r.uid !== uid && !String(r.intendedRole || "").includes("educator"))
            .sort((a, b) => `${a.firstName || ""}${a.lastName || ""}`.localeCompare(`${b.firstName || ""}${b.lastName || ""}`))
        ),
      () => setRoster([])
    );
  }, [view, myCode, uid]);

  // the class goal (teacher and students both read it, live)
  useEffect(() => {
    if (!myCode) return undefined;
    return onSnapshot(
      doc(db, "educatorDirectory", myCode),
      (snap) => setGoal((snap.exists() && snap.data().mapGoals?.[subjectKey]) || null),
      () => setGoal(null)
    );
  }, [myCode, subjectKey]);

  // … and each student's finished games in this subject (live)
  const rosterKey = roster.map((r) => r.uid).join(",");
  useEffect(() => {
    if (view !== "educator" || !rosterKey) return undefined;
    const stops = rosterKey.split(",").map((kid) =>
      onSnapshot(
        doc(db, "userData", kid),
        (snap) => {
          const mine = ((snap.exists() && snap.data()) || {})[subjectKey] || {};
          const done = {};
          Object.entries(mine).forEach(([slug, e]) => {
            const g = e && typeof e === "object" ? e.game : null;
            if (typeof g === "boolean" ? g : Boolean(g?.completed)) done[slug] = true;
          });
          setKidProgress((prev) => ({ ...prev, [kid]: done }));
        },
        () => {}
      )
    );
    return () => stops.forEach((f) => f());
  }, [view, rosterKey, subjectKey]);

  const classmates = useMemo(
    () =>
      view === "educator"
        ? roster.map((r) => ({
            uid: r.uid,
            name: [r.firstName, r.lastName ? `${String(r.lastName)[0]}.` : ""].filter(Boolean).join(" ") || "Student",
            spot: r.mapSpots?.[subjectKey] || "",
            done: kidProgress[r.uid] || {},
          }))
        : null,
    [view, roster, kidProgress, subjectKey]
  );

  // STUDENT: remember where I'm standing, so my teacher sees me there
  const saveSpot = useCallback(
    (slug) => {
      if (view !== "student" || !uid || !myCode || !slug) return;
      setDoc(doc(db, "educatorRosters", myCode, "students", uid), { uid, mapSpots: { [subjectKey]: slug } }, { merge: true }).catch(() => {});
    },
    [view, uid, myCode, subjectKey]
  );

  const topics = useMemo(
    () =>
      orderTopics(layout?.topics || [], layout?.topicOrder).filter(
        (t) => t && t.slug && t.visible !== false && !(hiddenMap && hiddenMap[t.slug] === false)
      ),
    [layout, hiddenMap]
  );

  if (loading) return <div className="dynamic-treasure-page">Loading the treasure map…</div>;
  if (!layout) {
    return (
      <div className="dynamic-treasure-page">
        <div className="missing-layout-card">
          <h1>No treasure map for this class yet</h1>
          <button type="button" onClick={() => navigate(portalPath)}>
            Back to the class
          </button>
        </div>
      </div>
    );
  }

  const freeLimit = contentLimitForCourse ? contentLimitForCourse(grade) : contentLimit;

  return (
    <div className="dynamic-treasure-page tt-page">
      <AdminBackButton isAdmin={isAdmin} offset />
      <CornerCoins />
      <button type="button" className="tt-back" onClick={() => navigate(portalPath)}>
        ← {subjectLabel(subject)}
      </button>

      <TreasureTrail
        title={`${subjectLabel(subject)} Treasure Map`}
        topics={topics}
        lockedFrom={freeLimit === Infinity ? Infinity : freeLimit}
        progress={progress}
        targetSlug={targetSlug || null}
        spotKey={`sbl-map-spot:${uid}:${subjectKey}`}
        onArrive={saveSpot}
        classmates={classmates}
        goal={goal}
        onGoalClick={view === "educator" && myCode ? () => setGoalOpen(true) : undefined}
        onKidClick={view === "educator" ? (kid) => setKidOpen(kid) : undefined}
        onPlay={(slug) => navigate(`/games/${state}/${grade}/${subject}/${view}/${slug}?from=map`)}
        onLocked={() =>
          setPop({
            tone: "info",
            icon: "🔒",
            title: "This chest is locked",
            text: "This topic is part of a paid plan. Ask a grown-up about upgrading to open every chest!",
            button: "OK",
          })
        }
      />
      <FunPopup pop={pop} onClose={() => setPop(null)} />
      {goalOpen && (
        <GoalEditor
          subjectKey={subjectKey}
          goal={goal}
          classSize={classmates?.length || 0}
          chestsPerKid={topics.length}
          opened={(classmates || []).reduce((n, k) => n + topics.filter((t) => k.done?.[t.slug]).length, 0)}
          onClose={(text) => {
            setGoalOpen(false);
            if (text) setPop({ tone: "ok", title: "🎯 Done!", text });
          }}
        />
      )}
      {kidOpen && (
        <KidCard
          kid={(classmates || []).find((k) => k.uid === kidOpen)}
          topics={topics}
          subjectName={`${subjectLabel(subject)} treasure map`}
          onGiveCoins={(kid) => {
            setKidOpen("");
            setGiveTo([kid]);
          }}
          onClose={() => setKidOpen("")}
        />
      )}
      {giveTo && myCode && <GiveCoinsPopup code={myCode} preselect={giveTo} onClose={() => setGiveTo(null)} />}
    </div>
  );
};

export default DynamicTreasureMapPage;
